/**
 * Audit Logs Domain Seed Data
 *
 * Contains comprehensive audit trail data for:
 * - Case activities (created, updated, assigned, status changed)
 * - Consumer profile changes
 * - Document uploads and approvals
 * - User authentication events
 * - Team and permission changes
 * - Pipeline stage transitions
 *
 * This provides rich historical data for the Audit Logs view in Business Portal.
 */

import { seedId } from '../utils';
import { CASE_IDS } from './cases';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// AUDIT LOG IDs
// =============================================================================

export const BUSINESS_AUDIT_LOG_IDS = {
  // Case Events - VIP Consumer Marcus Thompson
  MARCUS_CASE_CREATED: seedId('audit-case-marcus-created', 1),
  MARCUS_CASE_ASSIGNED: seedId('audit-case-marcus-assigned', 2),
  MARCUS_STATUS_NEW_TO_PROGRESS: seedId('audit-case-marcus-status1', 3),
  MARCUS_PRIORITY_UPDATED: seedId('audit-case-marcus-priority', 4),
  MARCUS_COMMENT_ADDED: seedId('audit-case-marcus-comment', 5),
  MARCUS_DOC_UPLOADED: seedId('audit-case-marcus-doc', 6),
  MARCUS_DOC_APPROVED: seedId('audit-case-marcus-doc-approved', 7),

  // Case Events - Robert Chen
  ROBERT_CASE_CREATED: seedId('audit-case-robert-created', 1),
  ROBERT_DOC_UPLOAD_FAILED: seedId('audit-case-robert-upload-fail', 2),
  ROBERT_CASE_ESCALATED: seedId('audit-case-robert-escalated', 3),
  ROBERT_WIRE_CONFIRMED: seedId('audit-case-robert-wire', 4),

  // Case Events - David Williams (Urgent ACH)
  DAVID_CASE_CREATED: seedId('audit-case-david-created', 1),
  DAVID_PRIORITY_ESCALATED: seedId('audit-case-david-priority', 2),
  DAVID_BANKING_UPDATED: seedId('audit-case-david-banking', 3),
  DAVID_PAYMENT_RETRIED: seedId('audit-case-david-payment', 4),
  DAVID_CASE_RESOLVED: seedId('audit-case-david-resolved', 5),

  // Consumer Profile Events
  MARCUS_PROFILE_VIEWED: seedId('audit-consumer-marcus-view', 1),
  MARCUS_CONTACT_UPDATED: seedId('audit-consumer-marcus-contact', 2),
  ROBERT_PROFILE_ENRICHED: seedId('audit-consumer-robert-enrich', 1),
  ALICIA_TRUST_UPGRADED: seedId('audit-consumer-alicia-trust', 1),
  DAVID_BANKING_CHANGE: seedId('audit-consumer-david-bank', 1),
  JENNIFER_PROFILE_CREATED: seedId('audit-consumer-jennifer-create', 1),
  KEVIN_PROFILE_VERIFIED: seedId('audit-consumer-kevin-verify', 1),

  // Document Events
  DOC_TAX_RETURN_UPLOAD: seedId('audit-doc-tax-upload', 1),
  DOC_TAX_RETURN_ANALYZED: seedId('audit-doc-tax-analyzed', 2),
  DOC_TAX_RETURN_APPROVED: seedId('audit-doc-tax-approved', 3),
  DOC_INSURANCE_UPLOAD: seedId('audit-doc-insurance-upload', 1),
  DOC_INSURANCE_REJECTED: seedId('audit-doc-insurance-reject', 2),
  DOC_INSURANCE_RESUBMIT: seedId('audit-doc-insurance-resubmit', 3),
  DOC_INSURANCE_APPROVED: seedId('audit-doc-insurance-approved', 4),
  DOC_BANK_STMT_UPLOAD: seedId('audit-doc-bank-upload', 1),
  DOC_BANK_STMT_VERIFIED: seedId('audit-doc-bank-verify', 2),
  DOC_LLC_AGREEMENT_UPLOAD: seedId('audit-doc-llc-upload', 1),
  DOC_APPRAISAL_UPLOAD: seedId('audit-doc-appraisal-upload', 1),
  DOC_APPRAISAL_REVIEWED: seedId('audit-doc-appraisal-review', 2),

  // User Authentication Events
  AUTH_RACHEL_LOGIN: seedId('audit-auth-rachel-login', 1),
  AUTH_RACHEL_MFA_ENABLED: seedId('audit-auth-rachel-mfa', 2),
  AUTH_SARAH_LOGIN: seedId('audit-auth-sarah-login', 1),
  AUTH_SARAH_PASSWORD_CHANGE: seedId('audit-auth-sarah-pwd', 2),
  AUTH_MIKE_LOGIN_FAILED: seedId('audit-auth-mike-fail', 1),
  AUTH_MIKE_LOGIN_SUCCESS: seedId('audit-auth-mike-success', 2),
  AUTH_JAMES_SESSION_EXPIRED: seedId('audit-auth-james-expire', 1),
  AUTH_MARCUS_CONSUMER_LOGIN: seedId('audit-auth-marcus-consumer', 1),
  AUTH_ROBERT_CONSUMER_LOGIN: seedId('audit-auth-robert-consumer', 1),

  // Team & Permission Events
  TEAM_EMILY_INVITED: seedId('audit-team-emily-invite', 1),
  TEAM_EMILY_ACCEPTED: seedId('audit-team-emily-accept', 2),
  TEAM_EMILY_ROLE_CHANGED: seedId('audit-team-emily-role', 3),
  TEAM_JAMES_PERMISSIONS: seedId('audit-team-james-perms', 1),
  TEAM_NEW_ROLE_CREATED: seedId('audit-team-role-create', 1),
  TEAM_NMLS_LICENSE_ADDED: seedId('audit-team-nmls-add', 1),
  TEAM_STATE_LICENSE_VERIFIED: seedId('audit-team-state-verify', 1),

  // Pipeline Events
  PIPELINE_DEAL_STAGE_CHANGE_1: seedId('audit-pipe-stage-1', 1),
  PIPELINE_DEAL_STAGE_CHANGE_2: seedId('audit-pipe-stage-2', 2),
  PIPELINE_DEAL_STAGE_CHANGE_3: seedId('audit-pipe-stage-3', 3),
  PIPELINE_DEAL_APPROVED: seedId('audit-pipe-approved', 4),
  PIPELINE_DEAL_FUNDED: seedId('audit-pipe-funded', 5),
  PIPELINE_SLA_BREACH: seedId('audit-pipe-sla-breach', 1),
  PIPELINE_AUTOMATION_TRIGGERED: seedId('audit-pipe-auto', 1),

  // Lead & CRM Events
  LEAD_CREATED_JAMES: seedId('audit-lead-james-create', 1),
  LEAD_ENRICHED_JAMES: seedId('audit-lead-james-enrich', 2),
  LEAD_QUALIFIED_JAMES: seedId('audit-lead-james-qualify', 3),
  LEAD_CONVERTED_JAMES: seedId('audit-lead-james-convert', 4),
  LEAD_CREATED_EMILY: seedId('audit-lead-emily-create', 1),
  REFERRAL_SUBMITTED: seedId('audit-referral-submit', 1),
  REFERRAL_CONVERTED: seedId('audit-referral-convert', 2),
  COMMISSION_CALCULATED: seedId('audit-commission-calc', 1),
  COMMISSION_PAID: seedId('audit-commission-paid', 2),

  // System Events
  SYSTEM_BACKUP_COMPLETED: seedId('audit-system-backup', 1),
  SYSTEM_MAINTENANCE_START: seedId('audit-system-maint-start', 2),
  SYSTEM_MAINTENANCE_END: seedId('audit-system-maint-end', 3),
  SYSTEM_API_KEY_CREATED: seedId('audit-system-apikey', 1),
  SYSTEM_WEBHOOK_CONFIGURED: seedId('audit-system-webhook', 1),
  SYSTEM_INTEGRATION_ENABLED: seedId('audit-system-integ', 1),
} as const;

export type BusinessAuditLogId = (typeof BUSINESS_AUDIT_LOG_IDS)[keyof typeof BUSINESS_AUDIT_LOG_IDS];

// =============================================================================
// AUDIT LOG DATA TYPES
// =============================================================================

export type AuditEventType =
  | 'case_created'
  | 'case_updated'
  | 'case_assigned'
  | 'case_status_changed'
  | 'case_priority_changed'
  | 'case_resolved'
  | 'case_closed'
  | 'case_escalated'
  | 'comment_added'
  | 'consumer_created'
  | 'consumer_updated'
  | 'consumer_profile_viewed'
  | 'consumer_enriched'
  | 'consumer_trust_changed'
  | 'consumer_verified'
  | 'document_uploaded'
  | 'document_analyzed'
  | 'document_approved'
  | 'document_rejected'
  | 'document_viewed'
  | 'user_login'
  | 'user_logout'
  | 'user_login_failed'
  | 'user_mfa_enabled'
  | 'user_mfa_disabled'
  | 'user_password_changed'
  | 'session_expired'
  | 'team_member_invited'
  | 'team_member_joined'
  | 'team_member_removed'
  | 'role_assigned'
  | 'role_changed'
  | 'role_created'
  | 'permission_granted'
  | 'permission_revoked'
  | 'license_added'
  | 'license_verified'
  | 'pipeline_stage_changed'
  | 'deal_approved'
  | 'deal_funded'
  | 'sla_breach'
  | 'automation_triggered'
  | 'lead_created'
  | 'lead_enriched'
  | 'lead_qualified'
  | 'lead_converted'
  | 'referral_submitted'
  | 'referral_converted'
  | 'commission_calculated'
  | 'commission_paid'
  | 'system_backup'
  | 'system_maintenance'
  | 'api_key_created'
  | 'webhook_configured'
  | 'integration_enabled';

export type AuditEntityType =
  | 'case'
  | 'consumer'
  | 'document'
  | 'user'
  | 'team_member'
  | 'role'
  | 'pipeline'
  | 'deal'
  | 'lead'
  | 'referral'
  | 'commission'
  | 'system';

export type SeedAuditSeverity = 'info' | 'warning' | 'error' | 'critical';

export interface BusinessAuditLogSeedData {
  id: string;
  businessId: string;
  eventType: AuditEventType;
  entityType: AuditEntityType;
  entityId?: string;
  entityName?: string;
  description: string;
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
  actorType: 'user' | 'consumer' | 'system' | 'automation';
  severity: SeedAuditSeverity;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  daysAgo: number; // Days before "today" for timestamp calculation
}

// =============================================================================
// AUDIT LOG DATA
// =============================================================================

export const BUSINESS_AUDIT_LOGS: Record<keyof typeof BUSINESS_AUDIT_LOG_IDS, BusinessAuditLogSeedData> = {
  // ============================================================
  // CASE EVENTS - Marcus Thompson (VIP)
  // ============================================================
  MARCUS_CASE_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_created',
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_INSURANCE_CERT,
    entityName: 'Missing Insurance Certificate',
    description: 'Case created via borrower portal by VIP consumer Marcus Thompson',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    metadata: { caseType: 'Document Request', priority: 'high', channel: 'portal' },
    daysAgo: 5,
  },
  MARCUS_CASE_ASSIGNED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_CASE_ASSIGNED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_assigned',
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_INSURANCE_CERT,
    entityName: 'Missing Insurance Certificate',
    description: 'Case assigned to Rachel Martinez via VIP queue auto-routing',
    actorType: 'system',
    actorName: 'System',
    severity: 'info',
    metadata: { assignee: 'rachel.martinez@opsmatrix.local', reason: 'VIP_QUEUE' },
    daysAgo: 5,
  },
  MARCUS_STATUS_NEW_TO_PROGRESS: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_STATUS_NEW_TO_PROGRESS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_status_changed',
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_INSURANCE_CERT,
    entityName: 'Missing Insurance Certificate',
    description: 'Case status changed from New to In Progress',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { oldStatus: 'new', newStatus: 'in_progress' },
    daysAgo: 4,
  },
  MARCUS_PRIORITY_UPDATED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_PRIORITY_UPDATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_priority_changed',
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_INSURANCE_CERT,
    entityName: 'Missing Insurance Certificate',
    description: 'Case priority elevated to High due to VIP customer status',
    actorType: 'system',
    actorName: 'Automation',
    severity: 'warning',
    metadata: { oldPriority: 'medium', newPriority: 'high', reason: 'VIP_CUSTOMER' },
    daysAgo: 4,
  },
  MARCUS_COMMENT_ADDED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_COMMENT_ADDED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'comment_added',
    entityType: 'case',
    entityId: CASE_IDS.MARCUS_INSURANCE_CERT,
    entityName: 'Missing Insurance Certificate',
    description: 'Internal comment added: VIP client priority handling',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { isInternal: true, commentType: 'note' },
    daysAgo: 4,
  },
  MARCUS_DOC_UPLOADED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_DOC_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'insurance-certificate-1234-oak-valley.pdf',
    description: 'Document uploaded for case: Missing Insurance Certificate',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    metadata: { fileSize: 245760, fileType: 'application/pdf', caseId: CASE_IDS.MARCUS_INSURANCE_CERT },
    daysAgo: 3,
  },
  MARCUS_DOC_APPROVED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_DOC_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_approved',
    entityType: 'document',
    entityName: 'insurance-certificate-1234-oak-valley.pdf',
    description: 'Document approved by Rachel Martinez',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { reviewTime: '2 hours', aiConfidence: 0.95 },
    daysAgo: 3,
  },

  // ============================================================
  // CASE EVENTS - Robert Chen
  // ============================================================
  ROBERT_CASE_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.ROBERT_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_created',
    entityType: 'case',
    entityId: CASE_IDS.ROBERT_TAX_UPLOAD,
    entityName: 'Tax Return Upload Issue',
    description: 'Case created via email by Robert Chen - file size error reported',
    actorId: CONSUMER_IDS.ROBERT_CHEN,
    actorEmail: 'robert.chen@chenenterprises.com',
    actorName: 'Robert Chen',
    actorType: 'consumer',
    severity: 'info',
    metadata: { caseType: 'Document Request', channel: 'email' },
    daysAgo: 7,
  },
  ROBERT_DOC_UPLOAD_FAILED: {
    id: BUSINESS_AUDIT_LOG_IDS.ROBERT_DOC_UPLOAD_FAILED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'chen-enterprises-2024-tax-return.pdf',
    description: 'Document upload failed - file size exceeded 25MB limit',
    actorId: CONSUMER_IDS.ROBERT_CHEN,
    actorEmail: 'robert.chen@chenenterprises.com',
    actorName: 'Robert Chen',
    actorType: 'consumer',
    severity: 'warning',
    metadata: { attemptedSize: 47185920, maxAllowed: 26214400, reason: 'FILE_SIZE_EXCEEDED' },
    daysAgo: 7,
  },
  ROBERT_CASE_ESCALATED: {
    id: BUSINESS_AUDIT_LOG_IDS.ROBERT_CASE_ESCALATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_escalated',
    entityType: 'case',
    entityId: CASE_IDS.ROBERT_TAX_UPLOAD,
    entityName: 'Tax Return Upload Issue',
    description: 'Upload limit increased to 100MB for VIP customer',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
    severity: 'info',
    metadata: { oldLimit: 25, newLimit: 100, unit: 'MB' },
    daysAgo: 6,
  },
  ROBERT_WIRE_CONFIRMED: {
    id: BUSINESS_AUDIT_LOG_IDS.ROBERT_WIRE_CONFIRMED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_resolved',
    entityType: 'case',
    entityId: CASE_IDS.ROBERT_WIRE_CONFIRM,
    entityName: 'Wire Transfer Confirmation',
    description: 'Wire transfer confirmed and case resolved',
    actorId: USER_IDS.JAMES_WILSON,
    actorEmail: 'james.wilson@opsmatrix.local',
    actorName: 'James Wilson',
    actorType: 'user',
    severity: 'info',
    metadata: { wireRef: 'FED20240115-7823', amount: 342567.89 },
    daysAgo: 2,
  },

  // ============================================================
  // CASE EVENTS - David Williams (Urgent ACH)
  // ============================================================
  DAVID_CASE_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_created',
    entityType: 'case',
    entityId: CASE_IDS.DAVID_ACH_FAILED,
    entityName: 'ACH Payment Failed - Urgent',
    description: 'Urgent case created via phone support - ACH rejection',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
    severity: 'warning',
    metadata: { caseType: 'Payment Issue', priority: 'urgent', channel: 'phone' },
    daysAgo: 3,
  },
  DAVID_PRIORITY_ESCALATED: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_PRIORITY_ESCALATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_priority_changed',
    entityType: 'case',
    entityId: CASE_IDS.DAVID_ACH_FAILED,
    entityName: 'ACH Payment Failed - Urgent',
    description: 'Case escalated to urgent priority - payment deadline approaching',
    actorType: 'system',
    actorName: 'Automation',
    severity: 'critical',
    metadata: { oldPriority: 'high', newPriority: 'urgent', hoursToDeadline: 24 },
    daysAgo: 3,
  },
  DAVID_BANKING_UPDATED: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_BANKING_UPDATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_updated',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.DAVID_WILLIAMS,
    entityName: 'David Williams',
    description: 'Banking information updated for ACH payments',
    actorId: USER_IDS.JAMES_WILSON,
    actorEmail: 'james.wilson@opsmatrix.local',
    actorName: 'James Wilson',
    actorType: 'user',
    severity: 'info',
    metadata: { field: 'bankAccount', masked: '****4521' },
    daysAgo: 2,
  },
  DAVID_PAYMENT_RETRIED: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_PAYMENT_RETRIED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'automation_triggered',
    entityType: 'case',
    entityId: CASE_IDS.DAVID_ACH_FAILED,
    entityName: 'ACH Payment Failed - Urgent',
    description: 'ACH payment retry initiated with updated banking info',
    actorType: 'automation',
    actorName: 'Payment Retry Automation',
    severity: 'info',
    metadata: { paymentMethod: 'ACH', retryCount: 1 },
    daysAgo: 2,
  },
  DAVID_CASE_RESOLVED: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_CASE_RESOLVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'case_resolved',
    entityType: 'case',
    entityId: CASE_IDS.DAVID_ACH_FAILED,
    entityName: 'ACH Payment Failed - Urgent',
    description: 'ACH payment successful - case resolved',
    actorType: 'system',
    actorName: 'System',
    severity: 'info',
    metadata: { paymentStatus: 'success', resolvedWithin: '48 hours' },
    daysAgo: 1,
  },

  // ============================================================
  // CONSUMER PROFILE EVENTS
  // ============================================================
  MARCUS_PROFILE_VIEWED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_PROFILE_VIEWED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_profile_viewed',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.MARCUS_THOMPSON,
    entityName: 'Marcus Thompson',
    description: 'Consumer profile accessed by Rachel Martinez',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    ipAddress: '10.0.1.45',
    daysAgo: 4,
  },
  MARCUS_CONTACT_UPDATED: {
    id: BUSINESS_AUDIT_LOG_IDS.MARCUS_CONTACT_UPDATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_updated',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.MARCUS_THOMPSON,
    entityName: 'Marcus Thompson',
    description: 'Consumer updated phone number via self-service portal',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    metadata: { field: 'phone', newValue: '+1-512-555-1001' },
    daysAgo: 10,
  },
  ROBERT_PROFILE_ENRICHED: {
    id: BUSINESS_AUDIT_LOG_IDS.ROBERT_PROFILE_ENRICHED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_enriched',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.ROBERT_CHEN,
    entityName: 'Robert Chen',
    description: 'Consumer profile enriched via 3rd-party API',
    actorType: 'system',
    actorName: 'Enrichment Service',
    severity: 'info',
    metadata: { source: 'ZoomInfo', fieldsEnriched: ['company', 'title', 'linkedIn'] },
    daysAgo: 15,
  },
  ALICIA_TRUST_UPGRADED: {
    id: BUSINESS_AUDIT_LOG_IDS.ALICIA_TRUST_UPGRADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_trust_changed',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    entityName: 'Alicia Fernandez',
    description: 'Consumer trust tier upgraded to Trusted based on completed deals',
    actorType: 'system',
    actorName: 'Trust Score Engine',
    severity: 'info',
    metadata: { oldTier: 'Established', newTier: 'Trusted', trustScore: 105, completedDeals: 7 },
    daysAgo: 30,
  },
  DAVID_BANKING_CHANGE: {
    id: BUSINESS_AUDIT_LOG_IDS.DAVID_BANKING_CHANGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_updated',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.DAVID_WILLIAMS,
    entityName: 'David Williams',
    description: 'Consumer banking information updated - requires verification',
    actorId: USER_IDS.JAMES_WILSON,
    actorEmail: 'james.wilson@opsmatrix.local',
    actorName: 'James Wilson',
    actorType: 'user',
    severity: 'warning',
    metadata: { field: 'bankAccount', verificationRequired: true },
    daysAgo: 2,
  },
  JENNIFER_PROFILE_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.JENNIFER_PROFILE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_created',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    entityName: 'Jennifer Martinez',
    description: 'New consumer profile created via onboarding wizard',
    actorId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    actorEmail: 'jennifer.martinez@gmail.com',
    actorName: 'Jennifer Martinez',
    actorType: 'consumer',
    severity: 'info',
    metadata: { source: 'onboarding', trustTier: 'Standard' },
    daysAgo: 14,
  },
  KEVIN_PROFILE_VERIFIED: {
    id: BUSINESS_AUDIT_LOG_IDS.KEVIN_PROFILE_VERIFIED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'consumer_verified',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.KEVIN_JOHNSON,
    entityName: 'Kevin Johnson',
    description: 'Consumer identity verified via document submission',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { verificationType: 'ID_DOCUMENT', documentType: 'drivers_license' },
    daysAgo: 8,
  },

  // ============================================================
  // DOCUMENT EVENTS
  // ============================================================
  DOC_TAX_RETURN_UPLOAD: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_TAX_RETURN_UPLOAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'chen-enterprises-2024-tax-return.pdf',
    description: '2024 tax return uploaded successfully after limit increase',
    actorId: CONSUMER_IDS.ROBERT_CHEN,
    actorEmail: 'robert.chen@chenenterprises.com',
    actorName: 'Robert Chen',
    actorType: 'consumer',
    severity: 'info',
    metadata: { fileSize: 47185920, category: 'tax_return' },
    daysAgo: 5,
  },
  DOC_TAX_RETURN_ANALYZED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_TAX_RETURN_ANALYZED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_analyzed',
    entityType: 'document',
    entityName: 'chen-enterprises-2024-tax-return.pdf',
    description: 'Document analyzed by AI - fields extracted successfully',
    actorType: 'system',
    actorName: 'Document AI',
    severity: 'info',
    metadata: { fieldsExtracted: 24, confidence: 0.92, processingTime: '15 seconds' },
    daysAgo: 5,
  },
  DOC_TAX_RETURN_APPROVED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_TAX_RETURN_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_approved',
    entityType: 'document',
    entityName: 'chen-enterprises-2024-tax-return.pdf',
    description: 'Tax return document approved after review',
    actorId: USER_IDS.JAMES_WILSON,
    actorEmail: 'james.wilson@opsmatrix.local',
    actorName: 'James Wilson',
    actorType: 'user',
    severity: 'info',
    metadata: { reviewTime: '45 minutes' },
    daysAgo: 4,
  },
  DOC_INSURANCE_UPLOAD: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_INSURANCE_UPLOAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'property-insurance-expired.pdf',
    description: 'Insurance certificate uploaded - expired document detected',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'warning',
    metadata: { fileSize: 156000, category: 'insurance', expirationDate: '2025-01-15' },
    daysAgo: 6,
  },
  DOC_INSURANCE_REJECTED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_INSURANCE_REJECTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_rejected',
    entityType: 'document',
    entityName: 'property-insurance-expired.pdf',
    description: 'Insurance certificate rejected - document expired',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'warning',
    metadata: { rejectionReason: 'DOCUMENT_EXPIRED', requiredAction: 'RESUBMIT' },
    daysAgo: 6,
  },
  DOC_INSURANCE_RESUBMIT: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_INSURANCE_RESUBMIT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'property-insurance-current.pdf',
    description: 'Updated insurance certificate resubmitted',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    metadata: { resubmission: true, previousDocument: 'property-insurance-expired.pdf' },
    daysAgo: 4,
  },
  DOC_INSURANCE_APPROVED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_INSURANCE_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_approved',
    entityType: 'document',
    entityName: 'property-insurance-current.pdf',
    description: 'Updated insurance certificate approved',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { expirationDate: '2026-01-15', coverageAmount: 1500000 },
    daysAgo: 3,
  },
  DOC_BANK_STMT_UPLOAD: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_BANK_STMT_UPLOAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'bank-statement-dec-2025.pdf',
    description: 'Bank statement uploaded for loan application',
    actorId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    actorEmail: 'alicia.fernandez@sunbeltdev.com',
    actorName: 'Alicia Fernandez',
    actorType: 'consumer',
    severity: 'info',
    metadata: { category: 'bank_statement', period: 'December 2025' },
    daysAgo: 12,
  },
  DOC_BANK_STMT_VERIFIED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_BANK_STMT_VERIFIED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_approved',
    entityType: 'document',
    entityName: 'bank-statement-dec-2025.pdf',
    description: 'Bank statement verified - account balance confirmed',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { verificationMethod: 'plaid', balanceConfirmed: true },
    daysAgo: 11,
  },
  DOC_LLC_AGREEMENT_UPLOAD: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_LLC_AGREEMENT_UPLOAD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'santos-ventures-ii-operating-agreement.pdf',
    description: 'LLC operating agreement uploaded for entity verification',
    actorId: CONSUMER_IDS.MARIA_SANTOS,
    actorEmail: 'maria.santos@santosventures.com',
    actorName: 'Maria Santos',
    actorType: 'consumer',
    severity: 'info',
    metadata: { entityName: 'Santos Ventures II LLC', documentType: 'operating_agreement' },
    daysAgo: 8,
  },
  DOC_APPRAISAL_UPLOAD: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_APPRAISAL_UPLOAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_uploaded',
    entityType: 'document',
    entityName: 'appraisal-1234-oak-street.pdf',
    description: 'Property appraisal report uploaded',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    metadata: { propertyAddress: '1234 Oak Street, Austin, TX', appraisedValue: 575000 },
    daysAgo: 20,
  },
  DOC_APPRAISAL_REVIEWED: {
    id: BUSINESS_AUDIT_LOG_IDS.DOC_APPRAISAL_REVIEWED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'document_approved',
    entityType: 'document',
    entityName: 'appraisal-1234-oak-street.pdf',
    description: 'Appraisal reviewed and approved by underwriting',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
    severity: 'info',
    metadata: { approvedValue: 575000, ltv: 0.59 },
    daysAgo: 18,
  },

  // ============================================================
  // USER AUTHENTICATION EVENTS
  // ============================================================
  AUTH_RACHEL_LOGIN: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_RACHEL_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login',
    entityType: 'user',
    entityId: USER_IDS.RACHEL_MARTINEZ,
    entityName: 'Rachel Martinez',
    description: 'User logged in successfully',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    ipAddress: '10.0.1.45',
    userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
    daysAgo: 0,
  },
  AUTH_RACHEL_MFA_ENABLED: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_RACHEL_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_mfa_enabled',
    entityType: 'user',
    entityId: USER_IDS.RACHEL_MARTINEZ,
    entityName: 'Rachel Martinez',
    description: 'Multi-factor authentication enabled for user account',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { mfaType: 'TOTP', backupCodesGenerated: 10 },
    daysAgo: 45,
  },
  AUTH_SARAH_LOGIN: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_SARAH_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login',
    entityType: 'user',
    entityId: USER_IDS.SARAH_CHEN,
    entityName: 'Sarah Chen',
    description: 'User logged in successfully',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    ipAddress: '10.0.1.52',
    daysAgo: 0,
  },
  AUTH_SARAH_PASSWORD_CHANGE: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_SARAH_PASSWORD_CHANGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_password_changed',
    entityType: 'user',
    entityId: USER_IDS.SARAH_CHEN,
    entityName: 'Sarah Chen',
    description: 'User password changed successfully',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { initiatedBy: 'self', reason: 'scheduled_rotation' },
    daysAgo: 30,
  },
  AUTH_MIKE_LOGIN_FAILED: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_MIKE_LOGIN_FAILED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login_failed',
    entityType: 'user',
    entityName: 'mike.johnson@opsmatrix.local',
    description: 'Login attempt failed - incorrect password',
    actorType: 'user',
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    severity: 'warning',
    ipAddress: '10.0.1.78',
    metadata: { failureReason: 'INVALID_PASSWORD', attemptCount: 1 },
    daysAgo: 1,
  },
  AUTH_MIKE_LOGIN_SUCCESS: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_MIKE_LOGIN_SUCCESS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login',
    entityType: 'user',
    entityId: USER_IDS.MIKE_JOHNSON,
    entityName: 'Mike Johnson',
    description: 'User logged in successfully after password correction',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
    severity: 'info',
    ipAddress: '10.0.1.78',
    daysAgo: 1,
  },
  AUTH_JAMES_SESSION_EXPIRED: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_JAMES_SESSION_EXPIRED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'session_expired',
    entityType: 'user',
    entityId: USER_IDS.JAMES_WILSON,
    entityName: 'James Wilson',
    description: 'User session expired due to inactivity',
    actorType: 'system',
    actorName: 'Session Manager',
    severity: 'info',
    metadata: { sessionDuration: '8 hours', inactivityPeriod: '2 hours' },
    daysAgo: 2,
  },
  AUTH_MARCUS_CONSUMER_LOGIN: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_MARCUS_CONSUMER_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.MARCUS_THOMPSON,
    entityName: 'Marcus Thompson',
    description: 'Consumer logged into borrower portal',
    actorId: CONSUMER_IDS.MARCUS_THOMPSON,
    actorEmail: 'marcus.thompson@investorpro.com',
    actorName: 'Marcus Thompson',
    actorType: 'consumer',
    severity: 'info',
    ipAddress: '72.14.32.105',
    userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_2)',
    daysAgo: 3,
  },
  AUTH_ROBERT_CONSUMER_LOGIN: {
    id: BUSINESS_AUDIT_LOG_IDS.AUTH_ROBERT_CONSUMER_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'user_login',
    entityType: 'consumer',
    entityId: CONSUMER_IDS.ROBERT_CHEN,
    entityName: 'Robert Chen',
    description: 'Consumer logged into borrower portal',
    actorId: CONSUMER_IDS.ROBERT_CHEN,
    actorEmail: 'robert.chen@chenenterprises.com',
    actorName: 'Robert Chen',
    actorType: 'consumer',
    severity: 'info',
    ipAddress: '98.45.12.87',
    daysAgo: 5,
  },

  // ============================================================
  // TEAM & PERMISSION EVENTS
  // ============================================================
  TEAM_EMILY_INVITED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_EMILY_INVITED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'team_member_invited',
    entityType: 'team_member',
    entityName: 'emily.rodriguez@opsmatrix.local',
    description: 'Team member invitation sent to Emily Rodriguez',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { role: 'member', department: 'Processing' },
    daysAgo: 21,
  },
  TEAM_EMILY_ACCEPTED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_EMILY_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'team_member_joined',
    entityType: 'team_member',
    entityName: 'Emily Rodriguez',
    description: 'Emily Rodriguez accepted team invitation and joined',
    actorEmail: 'emily.rodriguez@opsmatrix.local',
    actorName: 'Emily Rodriguez',
    actorType: 'user',
    severity: 'info',
    metadata: { role: 'member', department: 'Processing' },
    daysAgo: 20,
  },
  TEAM_EMILY_ROLE_CHANGED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_EMILY_ROLE_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'role_changed',
    entityType: 'team_member',
    entityName: 'Emily Rodriguez',
    description: 'Emily Rodriguez promoted from member to manager',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { oldRole: 'member', newRole: 'manager', reason: 'performance_promotion' },
    daysAgo: 5,
  },
  TEAM_JAMES_PERMISSIONS: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_JAMES_PERMISSIONS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'permission_granted',
    entityType: 'team_member',
    entityId: USER_IDS.JAMES_WILSON,
    entityName: 'James Wilson',
    description: 'Additional permissions granted for payment processing',
    actorId: USER_IDS.SUPER_ADMIN,
    actorName: 'Super Admin',
    actorType: 'user',
    severity: 'info',
    metadata: { permissions: ['PAYMENT_PROCESS', 'PAYMENT_APPROVE'], reason: 'role_expansion' },
    daysAgo: 15,
  },
  TEAM_NEW_ROLE_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_NEW_ROLE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'role_created',
    entityType: 'role',
    entityName: 'Senior Underwriter',
    description: 'New custom role created: Senior Underwriter',
    actorId: USER_IDS.SUPER_ADMIN,
    actorName: 'Super Admin',
    actorType: 'user',
    severity: 'info',
    metadata: { permissionCount: 18, basedOn: 'underwriter' },
    daysAgo: 60,
  },
  TEAM_NMLS_LICENSE_ADDED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_NMLS_LICENSE_ADDED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'license_added',
    entityType: 'team_member',
    entityId: USER_IDS.RACHEL_MARTINEZ,
    entityName: 'Rachel Martinez',
    description: 'NMLS license added for loan origination permissions',
    actorId: USER_IDS.SUPER_ADMIN,
    actorName: 'Super Admin',
    actorType: 'user',
    severity: 'info',
    metadata: { licenseType: 'NMLS_INDIVIDUAL', nmlsId: '1234567', expirationDate: '2027-12-31' },
    daysAgo: 90,
  },
  TEAM_STATE_LICENSE_VERIFIED: {
    id: BUSINESS_AUDIT_LOG_IDS.TEAM_STATE_LICENSE_VERIFIED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'license_verified',
    entityType: 'team_member',
    entityId: USER_IDS.SARAH_CHEN,
    entityName: 'Sarah Chen',
    description: 'Texas state loan officer license verified',
    actorType: 'system',
    actorName: 'License Verification Service',
    severity: 'info',
    metadata: { state: 'TX', licenseType: 'STATE_LOAN_OFFICER', validUntil: '2026-06-30' },
    daysAgo: 45,
  },

  // ============================================================
  // PIPELINE EVENTS
  // ============================================================
  PIPELINE_DEAL_STAGE_CHANGE_1: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_DEAL_STAGE_CHANGE_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'pipeline_stage_changed',
    entityType: 'case',
    entityName: 'Walker Bridge Loan - $340,000',
    description: 'Deal moved from Application to Document Collection',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { oldStage: 'Application', newStage: 'Document Collection', daysInPreviousStage: 2 },
    daysAgo: 25,
  },
  PIPELINE_DEAL_STAGE_CHANGE_2: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_DEAL_STAGE_CHANGE_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'pipeline_stage_changed',
    entityType: 'case',
    entityName: 'Walker Bridge Loan - $340,000',
    description: 'Deal moved from Document Collection to Underwriting',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { oldStage: 'Document Collection', newStage: 'Underwriting', daysInPreviousStage: 5 },
    daysAgo: 18,
  },
  PIPELINE_DEAL_STAGE_CHANGE_3: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_DEAL_STAGE_CHANGE_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'pipeline_stage_changed',
    entityType: 'case',
    entityName: 'Chen Multi-Family - $937,500',
    description: 'Deal moved from Application to Document Collection',
    actorId: USER_IDS.MIKE_JOHNSON,
    actorEmail: 'mike.johnson@opsmatrix.local',
    actorName: 'Mike Johnson',
    actorType: 'user',
    severity: 'info',
    metadata: { oldStage: 'Application', newStage: 'Document Collection', borrower: 'Emily Chen' },
    daysAgo: 12,
  },
  PIPELINE_DEAL_APPROVED: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_DEAL_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'deal_approved',
    entityType: 'case',
    entityName: 'Thompson Bridge Loan - $485,000',
    description: 'Deal approved by underwriting - ready for funding',
    actorId: USER_IDS.JAMES_WILSON,
    actorEmail: 'james.wilson@opsmatrix.local',
    actorName: 'James Wilson',
    actorType: 'user',
    severity: 'info',
    metadata: { approvedAmount: 485000, interestRate: 0.0725, term: 12 },
    daysAgo: 45,
  },
  PIPELINE_DEAL_FUNDED: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_DEAL_FUNDED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'deal_funded',
    entityType: 'case',
    entityName: 'Thompson Bridge Loan - $485,000',
    description: 'Deal funded successfully - wire sent',
    actorType: 'system',
    actorName: 'Funding System',
    severity: 'info',
    metadata: { fundedAmount: 485000, wireRef: 'FED20251215-9845', fundingDate: '2025-12-15' },
    daysAgo: 40,
  },
  PIPELINE_SLA_BREACH: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_SLA_BREACH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'sla_breach',
    entityType: 'case',
    entityName: 'Rivera Refinance - $450,000',
    description: 'SLA breach warning - deal in Pre-Approval stage for 5+ days',
    actorType: 'system',
    actorName: 'SLA Monitor',
    severity: 'warning',
    metadata: { stage: 'Pre-Approval', daysInStage: 5, slaLimit: 3 },
    daysAgo: 8,
  },
  PIPELINE_AUTOMATION_TRIGGERED: {
    id: BUSINESS_AUDIT_LOG_IDS.PIPELINE_AUTOMATION_TRIGGERED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'automation_triggered',
    entityType: 'pipeline',
    entityName: 'Auto-Assignment Rule',
    description: 'Automation triggered: New deal auto-assigned to Sarah Chen',
    actorType: 'automation',
    actorName: 'Deal Assignment Automation',
    severity: 'info',
    metadata: { rule: 'round_robin', assignee: 'sarah.chen@opsmatrix.local' },
    daysAgo: 10,
  },

  // ============================================================
  // LEAD & CRM EVENTS
  // ============================================================
  LEAD_CREATED_JAMES: {
    id: BUSINESS_AUDIT_LOG_IDS.LEAD_CREATED_JAMES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'lead_created',
    entityType: 'lead',
    entityName: 'James Walker - Walker Investment Group',
    description: 'New lead created from website inquiry',
    actorType: 'system',
    actorName: 'Lead Capture',
    severity: 'info',
    metadata: { source: 'Website Inquiry', initialScore: 75, loanAmount: 340000 },
    daysAgo: 35,
  },
  LEAD_ENRICHED_JAMES: {
    id: BUSINESS_AUDIT_LOG_IDS.LEAD_ENRICHED_JAMES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'lead_enriched',
    entityType: 'lead',
    entityName: 'James Walker - Walker Investment Group',
    description: 'Lead enriched with company and contact data',
    actorType: 'system',
    actorName: 'Enrichment Service',
    severity: 'info',
    metadata: { source: 'ZoomInfo', fieldsEnriched: ['company', 'revenue', 'employees'] },
    daysAgo: 34,
  },
  LEAD_QUALIFIED_JAMES: {
    id: BUSINESS_AUDIT_LOG_IDS.LEAD_QUALIFIED_JAMES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'lead_qualified',
    entityType: 'lead',
    entityName: 'James Walker - Walker Investment Group',
    description: 'Lead qualified as SQL - score increased to 92',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { qualification: 'SQL', score: 92, reason: 'experienced_investor' },
    daysAgo: 30,
  },
  LEAD_CONVERTED_JAMES: {
    id: BUSINESS_AUDIT_LOG_IDS.LEAD_CONVERTED_JAMES,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'lead_converted',
    entityType: 'lead',
    entityName: 'James Walker - Walker Investment Group',
    description: 'Lead converted to deal - Walker Bridge Loan',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { dealAmount: 340000, loanType: 'bridge', conversionTime: '5 days' },
    daysAgo: 28,
  },
  LEAD_CREATED_EMILY: {
    id: BUSINESS_AUDIT_LOG_IDS.LEAD_CREATED_EMILY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'lead_created',
    entityType: 'lead',
    entityName: 'Emily Chen - Chen Realty Group',
    description: 'New lead created from partner referral',
    actorType: 'system',
    actorName: 'Lead Capture',
    severity: 'info',
    metadata: { source: 'Partner Referral', partner: 'Capital Funding Partners', loanAmount: 937500 },
    daysAgo: 20,
  },
  REFERRAL_SUBMITTED: {
    id: BUSINESS_AUDIT_LOG_IDS.REFERRAL_SUBMITTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'referral_submitted',
    entityType: 'referral',
    entityName: 'Jennifer Martinez Referral',
    description: 'New referral submitted by Capital Funding Partners',
    actorType: 'user',
    actorName: 'Capital Funding Partners',
    severity: 'info',
    metadata: { referralType: 'buyer', clientEmail: 'jennifer.martinez@gmail.com' },
    daysAgo: 18,
  },
  REFERRAL_CONVERTED: {
    id: BUSINESS_AUDIT_LOG_IDS.REFERRAL_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'referral_converted',
    entityType: 'referral',
    entityName: 'Emily Chen Referral',
    description: 'Referral converted to deal - Chen Multi-Family Purchase',
    actorId: USER_IDS.SARAH_CHEN,
    actorEmail: 'sarah.chen@opsmatrix.local',
    actorName: 'Sarah Chen',
    actorType: 'user',
    severity: 'info',
    metadata: { dealAmount: 937500, partnerCommission: 4687.5 },
    daysAgo: 15,
  },
  COMMISSION_CALCULATED: {
    id: BUSINESS_AUDIT_LOG_IDS.COMMISSION_CALCULATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'commission_calculated',
    entityType: 'commission',
    entityName: 'Premier Mortgage - Thompson Deal',
    description: 'Commission calculated for funded Thompson Bridge Loan',
    actorType: 'system',
    actorName: 'Commission Engine',
    severity: 'info',
    metadata: { dealAmount: 485000, rate: 0.015, commissionAmount: 7275 },
    daysAgo: 40,
  },
  COMMISSION_PAID: {
    id: BUSINESS_AUDIT_LOG_IDS.COMMISSION_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'commission_paid',
    entityType: 'commission',
    entityName: 'Premier Mortgage - Thompson Deal',
    description: 'Commission paid to Premier Mortgage Partners',
    actorType: 'system',
    actorName: 'Payment System',
    severity: 'info',
    metadata: { amount: 7275, paymentMethod: 'ACH', paymentRef: 'COMM-2025-1231-001' },
    daysAgo: 25,
  },

  // ============================================================
  // SYSTEM EVENTS
  // ============================================================
  SYSTEM_BACKUP_COMPLETED: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_BACKUP_COMPLETED,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    eventType: 'system_backup',
    entityType: 'system',
    entityName: 'Database Backup',
    description: 'Nightly database backup completed successfully',
    actorType: 'system',
    actorName: 'Backup Service',
    severity: 'info',
    metadata: { backupSize: '2.4GB', duration: '15 minutes', location: 'gcs://backups' },
    daysAgo: 0,
  },
  SYSTEM_MAINTENANCE_START: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_MAINTENANCE_START,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    eventType: 'system_maintenance',
    entityType: 'system',
    entityName: 'Scheduled Maintenance',
    description: 'Scheduled maintenance window started',
    actorId: USER_IDS.SUPER_ADMIN,
    actorName: 'Super Admin',
    actorType: 'user',
    severity: 'warning',
    metadata: { reason: 'Database optimization', expectedDuration: '30 minutes' },
    daysAgo: 7,
  },
  SYSTEM_MAINTENANCE_END: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_MAINTENANCE_END,
    businessId: BUSINESS_IDS.PLATFORM_ADMIN,
    eventType: 'system_maintenance',
    entityType: 'system',
    entityName: 'Scheduled Maintenance',
    description: 'Maintenance window completed - all services restored',
    actorType: 'system',
    actorName: 'System',
    severity: 'info',
    metadata: { actualDuration: '25 minutes', status: 'success' },
    daysAgo: 7,
  },
  SYSTEM_API_KEY_CREATED: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_API_KEY_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'api_key_created',
    entityType: 'system',
    entityName: 'Production API Key',
    description: 'New API key created for integration',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { keyPrefix: 'ops_prod_', permissions: ['read', 'write'], expiresIn: '1 year' },
    daysAgo: 60,
  },
  SYSTEM_WEBHOOK_CONFIGURED: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_WEBHOOK_CONFIGURED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'webhook_configured',
    entityType: 'system',
    entityName: 'Deal Status Webhook',
    description: 'Webhook configured for deal status notifications',
    actorId: USER_IDS.RACHEL_MARTINEZ,
    actorEmail: 'rachel.martinez@opsmatrix.local',
    actorName: 'Rachel Martinez',
    actorType: 'user',
    severity: 'info',
    metadata: { url: 'https://crm.example.com/webhooks/deals', events: ['deal.created', 'deal.funded'] },
    daysAgo: 45,
  },
  SYSTEM_INTEGRATION_ENABLED: {
    id: BUSINESS_AUDIT_LOG_IDS.SYSTEM_INTEGRATION_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    eventType: 'integration_enabled',
    entityType: 'system',
    entityName: 'DocuSign Integration',
    description: 'DocuSign e-signature integration enabled',
    actorId: USER_IDS.SUPER_ADMIN,
    actorName: 'Super Admin',
    actorType: 'user',
    severity: 'info',
    metadata: { provider: 'DocuSign', features: ['signing', 'templates'] },
    daysAgo: 90,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all audit logs as an array for seeding
 */
export function getAllBusinessAuditLogs(): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS);
}

/**
 * Get audit logs by business
 */
export function getBusinessAuditLogsByBusiness(businessId: string): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.businessId === businessId);
}

/**
 * Get audit logs by entity type
 */
export function getBusinessAuditLogsByEntityType(entityType: AuditEntityType): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.entityType === entityType);
}

/**
 * Get audit logs by event type
 */
export function getBusinessAuditLogsByEventType(eventType: AuditEventType): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.eventType === eventType);
}

/**
 * Get audit logs by severity
 */
export function getBusinessAuditLogsBySeverity(severity: SeedAuditSeverity): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.severity === severity);
}

/**
 * Get audit logs for a specific actor
 */
export function getBusinessAuditLogsByActor(actorId: string): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.actorId === actorId);
}

/**
 * Get recent audit logs (within last N days)
 */
export function getRecentBusinessAuditLogs(daysBack: number): BusinessAuditLogSeedData[] {
  return Object.values(BUSINESS_AUDIT_LOGS).filter((log) => log.daysAgo <= daysBack);
}

/**
 * Calculate timestamp from daysAgo
 */
export function calculateAuditTimestamp(daysAgo: number): Date {
  const now = new Date();
  now.setDate(now.getDate() - daysAgo);
  // Add some randomness to hours for more realistic timestamps
  now.setHours(8 + Math.floor(Math.random() * 10)); // Business hours 8-18
  now.setMinutes(Math.floor(Math.random() * 60));
  return now;
}
